import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner, faApple, faGooglePlay } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import type { User } from '@/models';
import { APIRoutes } from '@/services/api.routes';
import { showToast } from '@/utils/toasts';
import { validateSCNUID, validateSCNToken } from '@/utils/helpers';

interface EditSCNConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onUserUpdated: (updatedUser: User) => void;
}

export default function EditSCNConfigModal({ isOpen, onClose, user, onUserUpdated }: EditSCNConfigModalProps) {
  const [scnUID, setScnUID] = useState('');
  const [scnToken, setScnToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ scnUID?: string; scnToken?: string }>({});
  const [apiError, setApiError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setScnUID('');
      setScnToken('');
      setErrors({});
      setApiError(null);
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: { scnUID?: string; scnToken?: string } = {};

    // Validate SCN UID
    const scnUIDValidation = validateSCNUID(scnUID);
    if (!scnUIDValidation.valid) {
      newErrors.scnUID = scnUIDValidation.error;
    }

    // Validate SCN Token
    const scnTokenValidation = validateSCNToken(scnToken);
    if (!scnTokenValidation.valid) {
      newErrors.scnToken = scnTokenValidation.error;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const updatedUser = await APIRoutes.updateCurrentUser({
        scnUID: scnUID.trim(),
        scnToken: scnToken.trim()
      });

      onUserUpdated(updatedUser);
      showToast('SCN-Konfiguration erfolgreich aktualisiert', 'success');
      onClose();
    } catch (error) {
      console.error('Error updating SCN config:', error);
      setApiError('Die SCN-Konfiguration konnte nicht gespeichert werden. Bitte überprüfen Sie Ihre Zugangsdaten.');
      showToast('Fehler beim Aktualisieren der SCN-Konfiguration', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const hasChanges =
    scnUID.trim() !== (user.scnUID || '') ||
    scnToken.trim() !== (user.scnToken || '');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="SCN-Konfiguration"
      icon={<FontAwesomeIcon icon={faEdit} className="w-5 h-5 text-blue-600" />}
      maxWidth="md"
      closeDisabled={isLoading}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          {/* Info Banner */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
              <strong>Simple Cloud Notifications</strong> ist eine Anwendung, mit der Sie die relevantesten Nachrichten in Echtzeit erhalten.
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Öffnen Sie die SCN-App, navigieren Sie zu <strong>Accounts</strong>, und kopieren Sie Ihre <strong>SCN UID</strong> und das <strong>SCN Token</strong>. Geben Sie diese unten ein, um die Verbindung herzustellen.
            </p>
          </div>

          {/* App Download Section */}
          <div className="mb-4">
            <div className="flex justify-center gap-3">
              <a
                href="https://apps.apple.com/de/app/simplecloudnotifier/id6455594868"
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 flex items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                title="App Store"
              >
                <FontAwesomeIcon icon={faApple} style={{ fontSize: 28 }} />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.blackforestbytes.simplecloudnotifier"
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 flex items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                title="Google Play"
              >
                <FontAwesomeIcon icon={faGooglePlay} style={{ fontSize: 28 }} />
              </a>
            </div>
          </div>

          {/* Error Banner */}
          {apiError !== null && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-800 dark:text-red-200">{apiError}</p>
            </div>
          )}

          {/* SCN UID */}
          <div>
            <label htmlFor="scnUID" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              SCN UID *
            </label>
            <input
              type="text"
              id="scnUID"
              value={scnUID}
              onChange={(e) => {
                setScnUID(e.target.value);
                setErrors({ ...errors, scnUID: undefined });
                setApiError(null);
              }}
              disabled={isLoading}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 ${
                errors.scnUID
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder="USR123456..."
              required
            />
            {errors.scnUID && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.scnUID}</p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Die SCN User ID muss mit "USR" beginnen
            </p>
          </div>

          {/* SCN Token */}
          <div>
            <label htmlFor="scnToken" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              SCN Token *
            </label>
            <input
              type="text"
              id="scnToken"
              value={scnToken}
              onChange={(e) => {
                setScnToken(e.target.value);
                setErrors({ ...errors, scnToken: undefined });
                setApiError(null);
              }}
              disabled={isLoading}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 font-mono text-sm ${
                errors.scnToken
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder="64 alphanumerische Zeichen..."
              required
            />
            {errors.scnToken && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.scnToken}</p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Der Token muss genau 64 alphanumerische Zeichen lang sein
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={isLoading || !hasChanges}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center"
          >
            {isLoading && <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />}
            Speichern
          </button>
        </div>
      </form>
    </Modal>
  );
}
